/**
 * Pure (React-free) story timeline, copy, captions and audio logic for clip-v2.
 * Deterministic: depends only on validated props + fps.
 *
 * Story: HOOK → SETUP → TWIST (2-6 shots) → PAYOFF → CTA → END CARD.
 * Every scene is made of "shots"; a shot is one visual state and never lasts
 * longer than MAX_SHOT_SECONDS, so there is a cut / visual change at least that often.
 */
import { normalizeCaptionWords } from "../../lib/captionLogic.js";
import { formatEmphasis, formatMoney, formatPrice } from "../scenarioExplainerTimeline.js";
import { stripHookMarkup } from "./clipV2Schema.js";

export const MAX_SHOT_SECONDS = 3;
export const END_CARD_SECONDS = 3;
export const MAX_CLIP_SECONDS = 60;
/** Narration must end this long before the end card. */
const AUDIO_TAIL_SECONDS = 0.35;

const TWIST_SHOTS = {
  "why-it-lost": [
    { key: "chart", seconds: 2.3 },
    { key: "move", seconds: 2.8, hitAt: 2.0 },
    { key: "why", seconds: 2.6, hitAt: 0.15 },
    { key: "breakeven", seconds: 2.4 },
  ],
  "compare-two": [
    { key: "split", seconds: 2.4 },
    { key: "downside", seconds: 2.8, hitAt: 0.9 },
    { key: "table", seconds: 2.8 },
    { key: "verdict", seconds: 2.2, hitAt: 0.15 },
  ],
  "change-one": [
    { key: "baseline", seconds: 2.4 },
    { key: "morph", seconds: 3.0, hitAt: 2.3 },
    { key: "delta", seconds: 2.6, hitAt: 0.15 },
  ],
};

/** Shot plan in seconds, before voiceover stretching. */
export function planShots(props) {
  const twist =
    props.format === "explainer"
      ? [
          { key: "chart", seconds: 2.3 },
          ...props.beats.map((beat, index) => ({
            key: `beat-${index}`,
            beatIndex: index,
            seconds: 2.8,
            hitAt: beat.emphasis ? 0.25 : undefined,
          })),
        ]
      : TWIST_SHOTS[props.format];
  return [
    { scene: "hook", key: "hook", seconds: 1.5, stretch: false },
    { scene: "setup", key: "setup", seconds: 2.6, stretch: true },
    ...twist.map((shot, twistIndex) => ({ scene: "twist", twistIndex, stretch: true, ...shot })),
    { scene: "payoff", key: "payoff", seconds: 2.6, hitAt: 0.35, stretch: true },
    { scene: "cta", key: "cta", seconds: 2.2, stretch: true },
    { scene: "end", key: "end", seconds: END_CARD_SECONDS, stretch: false },
  ];
}

/** Last spoken moment of the voiceover in seconds (0 when none). */
export function resolveVoiceoverSeconds(voiceover, probedAudioSeconds = 0) {
  if (!voiceover) return 0;
  const words = normalizeCaptionWords(voiceover.words);
  const lastWordEnd = words.length ? Math.max(...words.map((w) => w.endMs)) / 1000 : 0;
  return Math.max(lastWordEnd, Number(voiceover.durationSeconds) || 0, Number(probedAudioSeconds) || 0);
}

/**
 * Build the frame timeline. Stretchable shots grow proportionally so narration
 * ends before the end card; any shot longer than MAX_SHOT_SECONDS is split into
 * parts (each part is a new camera framing = a visual change).
 */
export function buildClipV2Timeline(props, fps, voiceoverSeconds = 0) {
  const plan = planShots(props);
  const sum = (shots) => shots.reduce((n, s) => n + s.seconds, 0);
  const endSeconds = plan[plan.length - 1].seconds;
  const content = plan.slice(0, -1);
  const fixed = sum(content.filter((s) => !s.stretch));
  const stretchable = sum(content.filter((s) => s.stretch));
  const needed = Math.min(voiceoverSeconds + AUDIO_TAIL_SECONDS, MAX_CLIP_SECONDS - endSeconds);
  const scale = needed > fixed + stretchable ? (needed - fixed) / stretchable : 1;

  const toFrames = (seconds) => Math.max(1, Math.round(seconds * fps));
  const maxShotFrames = Math.floor(MAX_SHOT_SECONDS * fps);
  const shots = [];
  const scenes = [];
  let cursor = 0;
  plan.forEach((shot) => {
    const total = toFrames(shot.seconds * (shot.stretch ? scale : 1));
    const parts = Math.max(1, Math.ceil(total / maxShotFrames));
    const shotFrom = cursor;
    for (let part = 0; part < parts; part += 1) {
      const a = Math.round((part * total) / parts);
      const b = Math.round(((part + 1) * total) / parts);
      shots.push({
        scene: shot.scene,
        key: shot.key,
        twistIndex: shot.twistIndex,
        beatIndex: shot.beatIndex,
        part,
        parts,
        from: shotFrom + a,
        durationInFrames: b - a,
        shotFrom,
        shotDurationInFrames: total,
        hitFrame: shot.hitAt != null && part === 0 ? shotFrom + Math.min(total - 1, toFrames(shot.hitAt * (shot.stretch ? scale : 1))) : null,
      });
    }
    const last = scenes[scenes.length - 1];
    if (last && last.scene === shot.scene) {
      last.durationInFrames += total;
    } else {
      scenes.push({ scene: shot.scene, from: shotFrom, durationInFrames: total });
    }
    cursor += total;
  });

  return { shots, scenes, totalFrames: cursor, fps, scale };
}

export function sceneOf(timeline, name) {
  return timeline.scenes.find((s) => s.scene === name);
}

/** Shots of one logical key (all parts), first part first. */
export function shotsOf(timeline, scene) {
  return timeline.shots.filter((s) => s.scene === scene);
}

export function findShot(timeline, frame) {
  const { shots } = timeline;
  for (let i = shots.length - 1; i >= 0; i -= 1) {
    if (frame >= shots[i].from) return shots[i];
  }
  return shots[0];
}

/** Typographic apostrophes (straight ' renders with a wide gap in heavy weights). */
export function smartQuotes(text) {
  return String(text).replace(/(\w)'(\w)/g, "$1’$2");
}

// ── Hook markup ─────────────────────────────────────────────

const LOSS_WORDS = /(lost|loss|lose|losing|down|fell|fall|drop|crash|crushed|−|-\$|-\d|decay)/i;

/**
 * Split hook text into animated tokens. Plain text → one token per word.
 * A `**…**` segment → one emphasis token (uppercase, on a single color slab,
 * loss-toned when it talks about losing). Punctuation that directly follows a
 * token is kept as that token's `suffix` (rendered after the slab).
 */
export function parseHookTokens(text) {
  const tokens = [];
  String(text)
    .split("**")
    .forEach((segment, index) => {
      const emphasis = index % 2 === 1;
      let rest = segment;
      if (!emphasis && tokens.length && /^[.,!?;:…)]+/.test(rest)) {
        const punct = rest.match(/^[.,!?;:…)]+/)[0];
        tokens[tokens.length - 1].suffix += punct;
        rest = rest.slice(punct.length);
      }
      const trimmed = rest.trim();
      if (!trimmed) return;
      if (emphasis) {
        tokens.push({ text: trimmed.toUpperCase(), emphasis: true, tone: LOSS_WORDS.test(trimmed) ? "loss" : "gain", suffix: "" });
        return;
      }
      trimmed
        .split(/\s+/)
        .filter(Boolean)
        .forEach((word) => tokens.push({ text: word, emphasis: false, tone: "gain", suffix: "" }));
    });
  return tokens;
}

export function selectedHook(props) {
  return props.hooks[Math.min(props.hookIndex, props.hooks.length - 1)];
}

/** Data-source tag shown next to the data-as-of line: "Modeled" or "Market data" (observed). */
export function dataLabelTag(dataLabel) {
  return dataLabel === "observed" ? "Market data" : "Modeled";
}

/** End-card data line. Observed clips are still a hypothetical position, so both keep "not actual trades". */
export function dataAsOfLine(dataAsOf, dataLabel = "modeled") {
  return dataLabel === "observed"
    ? `Data as of ${dataAsOf}. Based on market quotes, not actual trades.`
    : `Data as of ${dataAsOf}. Modeled results, not actual trades.`;
}

// ── Derived copy (from engine numbers only) ─────────────────

function pluralDays(days) {
  return `${days} day${days === 1 ? "" : "s"}`;
}

export function formatAssumptionValue(kind, value) {
  if (kind === "price") return formatPrice(value);
  if (kind === "time") return pluralDays(value);
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

/**
 * Label for the why-it-lost scenario price (scenarioMove.toSpot). "now" only when it IS the observed
 * current price (dataLabel "observed"); a modeled scenario price is labelled as a what-if
 * ("TSLA in 7 days" / "TSLA after move") so it is never read as today's real spot.
 */
export function scenarioPriceLabel(clip) {
  if (clip.dataLabel === "observed") return `${clip.ticker} now`;
  const days = clip.scenarioMove?.days;
  return days > 0 ? `${clip.ticker} in ${pluralDays(days)}` : `${clip.ticker} after move`;
}

export function assumptionLabel(assumption) {
  if (assumption.label) return assumption.label;
  return { price: "Stock price", time: "Days to expiration", volatility: "Implied volatility" }[assumption.kind];
}

/** Lower max loss wins; "Unlimited" always loses. */
export function compareVerdict(props) {
  const [a, b] = props.strategies;
  const loss = (s) => (s.numbers.maxLoss === "Unlimited" ? Infinity : Math.abs(s.numbers.maxLoss));
  const saferIndex = loss(a) <= loss(b) ? 0 : 1;
  const safer = props.strategies[saferIndex];
  const riskier = props.strategies[1 - saferIndex];
  const diff = Number.isFinite(loss(riskier)) ? loss(riskier) - loss(safer) : null;
  const text =
    props.verdict ??
    (diff != null ? `${safer.label} risks ${formatMoney(diff)} less` : `${safer.label} has a defined max loss`);
  return { saferIndex, safer, riskier, diff, text };
}

/** The clip's big number (payoff card + thumbnail). */
export function resolveHeadlineNumber(props) {
  if (props.lesson.number) return props.lesson.number;
  switch (props.format) {
    case "why-it-lost": {
      const { days } = props.scenarioMove;
      return {
        value: props.pnlAtMove,
        format: "signedCurrency",
        label: days > 0 ? `Modeled P&L after ${pluralDays(days)}` : "Modeled P&L after the move",
      };
    }
    case "compare-two": {
      const v = compareVerdict(props);
      if (v.diff != null) return { value: v.diff, format: "currency", label: `less max loss with the ${v.safer.label}` };
      return { value: Math.abs(v.safer.numbers.maxLoss), format: "currency", label: `${v.safer.label} max loss` };
    }
    case "change-one":
      return { value: props.changed.pnl - props.baseline.pnl, format: "signedCurrency", label: "Modeled P&L change" };
    case "explainer":
    default: {
      const beat = [...props.beats].reverse().find((b) => b.emphasis);
      if (beat) return beat.emphasis;
      const maxLoss = props.numbers.maxLoss;
      return { value: maxLoss === "Unlimited" ? 0 : -Math.abs(maxLoss), format: "signedCurrency", label: "Max loss" };
    }
  }
}

function signedPnl(value) {
  return formatMoney(Math.round(value), { signed: true });
}

/** Default caption/narration copy per scene, built from the validated numbers. */
export function defaultScript(props) {
  const headline = resolveHeadlineNumber(props);
  const label = headline.label ? ` ${headline.label.charAt(0).toLowerCase()}${headline.label.slice(1)}` : "";
  const payoff = `${formatEmphasis(headline)}${label}.`;
  const cta = `${props.cta.text}.`;
  switch (props.format) {
    case "why-it-lost": {
      const { fromSpot, toSpot, days, ivChangePct } = props.scenarioMove;
      const verb = toSpot > fromSpot ? "rose" : "fell";
      // Modeled: the move is a what-if, never narrated as something the stock did.
      const move =
        props.dataLabel === "observed"
          ? `${props.ticker} ${verb} from ${formatPrice(fromSpot)} to ${formatPrice(toSpot)}.`
          : `What if ${props.ticker} ${toSpot > fromSpot ? "rises" : "falls"} to ${formatPrice(toSpot)}${days > 0 ? ` in ${pluralDays(days)}` : ""}?`;
      const iv = ivChangePct ?? 0;
      let why;
      if (iv < 0) why = `Implied volatility fell ${Math.abs(iv)} points.${days > 0 ? " Time decay did the rest." : ""}`;
      else if (days > 0) why = `${pluralDays(days)} of time decay ate the gain.`;
      else why = "The move was not enough to cover the premium.";
      return {
        hook: null,
        setup: `The trade: a ${props.strategy.label} on ${props.ticker}.`,
        twist: [
          "Here's why it lost.",
          move,
          why,
          `At expiration it needs ${formatPrice(props.numbers.breakevens[0])} to break even.`,
        ],
        payoff,
        cta,
      };
    }
    case "compare-two": {
      const [a, b] = props.strategies;
      const loss = (s) => (s.numbers.maxLoss === "Unlimited" ? "unlimited" : formatMoney(Math.abs(s.numbers.maxLoss)));
      return {
        hook: null,
        setup: `Same view: ${props.view}. Two different trades.`,
        twist: [
          "Same direction. Different shape.",
          `Max loss: ${loss(a)} versus ${loss(b)}.`,
          "Now compare the upside and breakeven.",
          `${compareVerdict(props).text}.`,
        ],
        payoff,
        cta,
      };
    }
    case "change-one": {
      const { assumption, ticker } = props;
      const { kind } = assumption;
      const from = formatAssumptionValue(kind, assumption.from);
      const to = formatAssumptionValue(kind, assumption.to);
      const custom = assumption.label;
      let setup;
      let change;
      if (custom) {
        setup = `What if ${custom.toLowerCase()} goes from ${from} to ${to}?`;
        change = `Change one thing: ${custom.toLowerCase()}, ${from} to ${to}.`;
      } else if (kind === "time") {
        const diff = Math.abs(assumption.from - assumption.to);
        setup =
          assumption.to < assumption.from ? `What if ${pluralDays(diff)} pass?` : `What if you buy ${pluralDays(diff)} more?`;
        change = `Fast-forward: ${from} left becomes ${to}.`;
      } else if (kind === "volatility") {
        setup = `What if implied volatility goes from ${from} to ${to}?`;
        change = `Change one thing: IV ${from} to ${to}.`;
      } else {
        setup = `What if ${ticker} moves from ${from} to ${to}?`;
        change = `Move the stock: ${from} to ${to}.`;
      }
      const at = kind === "price" ? "" : ` at ${formatPrice(props.spot)}`;
      return {
        hook: null,
        setup,
        twist: [
          `Before: ${signedPnl(props.baseline.pnl)} P&L${at}.`,
          change,
          `After: ${signedPnl(props.changed.pnl)}. Nothing else changed.`,
        ],
        payoff,
        cta,
      };
    }
    case "explainer":
    default:
      return {
        hook: null,
        setup: `The trade: a ${props.strategy.label} on ${props.ticker}.`,
        twist: ["Here's the payoff at expiration.", ...props.beats.map((b) => b.text)],
        payoff,
        cta,
      };
  }
}

/** Script with per-scene overrides from props.script applied. */
export function resolveScript(props) {
  const base = defaultScript(props);
  const o = props.script || {};
  return {
    hook: o.hook ?? base.hook,
    setup: o.setup ?? base.setup,
    twist: o.twist ?? base.twist,
    payoff: o.payoff ?? base.payoff,
    cta: o.cta ?? base.cta,
  };
}

// ── Captions ────────────────────────────────────────────────

/**
 * Caption words (ms from video start). Voiceover word timings win; otherwise each
 * shot's script line is spread over that shot so captions follow the cuts.
 */
export function buildClipV2CaptionWords(props, timeline) {
  const spoken = normalizeCaptionWords(props.voiceover?.words);
  if (spoken.length) return spoken.map((w) => ({ ...w, group: null }));

  const script = resolveScript(props);
  const { fps } = timeline;
  const firstParts = timeline.shots.filter((s) => s.part === 0);
  const twistShots = firstParts.filter((s) => s.scene === "twist");
  const lineFor = (shot) => {
    if (shot.scene === "twist") {
      const lines = script.twist || [];
      if (shot.twistIndex < twistShots.length - 1) return lines[shot.twistIndex] ?? null;
      const rest = lines.slice(shot.twistIndex);
      return rest.length ? rest.join(" ") : null;
    }
    if (shot.scene === "end") return null;
    return script[shot.scene] ?? null;
  };

  const words = [];
  firstParts.forEach((shot, shotIndex) => {
    const line = lineFor(shot);
    if (!line) return;
    const tokens = smartQuotes(line).split(/\s+/).filter(Boolean);
    const startMs = (shot.shotFrom / fps) * 1000 + 180;
    const spanMs = (shot.shotDurationInFrames / fps) * 1000 * 0.86 - 180;
    const step = spanMs / tokens.length;
    tokens.forEach((token, i) => {
      words.push({
        text: token,
        startMs: Math.round(startMs + i * step),
        endMs: Math.round(startMs + (i + 1) * step),
        group: `shot-${shotIndex}`,
      });
    });
  });
  return words;
}

/**
 * Group words into 2-4 word caption chunks. Phrases break on group changes,
 * sentence punctuation and pauses; a phrase is split into evenly sized chunks
 * so there is never a lone orphan word when the phrase has 2+ words.
 */
export function buildClipV2CaptionChunks(words, { maxWords = 4, maxChars = 22, maxGapMs = 600 } = {}) {
  const phrases = [];
  let phrase = null;
  words.forEach((word, globalIndex) => {
    const prev = phrase?.[phrase.length - 1];
    const startsNew =
      !phrase || word.group !== prev.group || word.startMs - prev.endMs > maxGapMs || /[.!?:]$/.test(prev.text);
    if (startsNew) {
      phrase = [];
      phrases.push(phrase);
    }
    phrase.push({ ...word, globalIndex });
  });

  // Merge a 1-word phrase into its neighbour in the same group (keeps 2-4 words on screen).
  for (let i = 0; i < phrases.length; i += 1) {
    const p = phrases[i];
    if (p.length !== 1) continue;
    const prev = phrases[i - 1];
    const next = phrases[i + 1];
    if (next && next[0].group === p[0].group) {
      next.unshift(p[0]);
      phrases.splice(i, 1);
      i -= 1;
    } else if (prev && prev[0].group === p[0].group) {
      prev.push(p[0]);
      phrases.splice(i, 1);
      i -= 1;
    }
  }

  const chunks = [];
  phrases.forEach((items) => {
    const chars = items.reduce((n, w) => n + w.text.length, 0) + items.length - 1;
    let count = Math.max(Math.ceil(items.length / maxWords), Math.ceil(chars / maxChars), 1);
    // Never split into chunks smaller than 2 words.
    count = Math.min(count, Math.max(1, Math.floor(items.length / 2)));
    for (let c = 0; c < count; c += 1) {
      const slice = items.slice(Math.round((c * items.length) / count), Math.round(((c + 1) * items.length) / count));
      if (!slice.length) continue;
      chunks.push({
        group: slice[0].group,
        words: slice,
        startMs: slice[0].startMs,
        endMs: Math.max(...slice.map((w) => w.endMs)),
      });
    }
  });
  return chunks;
}

// ── Audio ───────────────────────────────────────────────────

/** Time ranges (seconds) where a voice is audible: narration words / span, unmuted presenter. */
export function voiceRanges(props, voiceoverSeconds) {
  const ranges = [];
  const words = normalizeCaptionWords(props.voiceover?.words);
  if (words.length) {
    let cur = null;
    words.forEach((w) => {
      const a = w.startMs / 1000;
      const b = w.endMs / 1000;
      if (cur && a - cur[1] < 0.6) cur[1] = Math.max(cur[1], b);
      else {
        cur = [a, b];
        ranges.push(cur);
      }
    });
  } else if (props.voiceover && voiceoverSeconds > 0) {
    ranges.push([0, voiceoverSeconds]);
  }
  if (props.presenter && !props.presenter.muted) {
    ranges.push([0, props.presenter.endSec - props.presenter.startSec]);
  }
  return ranges;
}

export const DUCK_FACTOR = 0.3;

/** Music volume at a frame: fade in/out, ducked (smoothly) while a voice is audible. */
export function musicVolumeAt(frame, { fps, totalFrames, volume, duck, ranges }) {
  const t = frame / fps;
  const ramp = 0.25;
  let duckAmount = 0;
  if (duck) {
    ranges.forEach(([a, b]) => {
      let d = 0;
      if (t >= a && t <= b) d = 1;
      else if (t < a && t > a - ramp) d = 1 - (a - t) / ramp;
      else if (t > b && t < b + ramp) d = 1 - (t - b) / ramp;
      duckAmount = Math.max(duckAmount, d);
    });
  }
  const fadeIn = Math.min(1, frame / Math.max(1, Math.round(fps * 0.4)));
  const fadeOut = Math.min(1, (totalFrames - frame) / Math.max(1, fps));
  const level = volume * (1 - duckAmount * (1 - DUCK_FACTOR));
  return Math.max(0, level * Math.max(0, Math.min(fadeIn, fadeOut)));
}

/** SFX cue frames: a whoosh leading into every cut, a hit on every number reveal. */
export function buildSfxCues(timeline) {
  const whoosh = timeline.shots.slice(1).map((s) => Math.max(0, s.from - 3));
  const hit = timeline.shots.map((s) => s.hitFrame).filter((f) => f != null);
  return { whoosh, hit };
}

export { stripHookMarkup };
